import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Section } from '../../components/AdminContentWrapper';
import { ADMIN_COLORS, darkFieldSx, outlinedButtonSx } from '../../components/adminStyles';
import { T } from '../../../../themes/tokens';
import SqlRulesService from '../../../../services/SqlRulesService';

const DAY_OPTIONS = [7, 30, 45];

const SOURCE_LABELS = {
  chat: 'chat',
  deep: 'Análisis Profundo',
  agent: 'chat y análisis',
  dashboard: 'tableros',
  workgroup: 'historial de Athena'
};

// "2 con pista · 1 repetido en el mismo turno": si el modelo volvió a
// cometer el error después de recibir la pista, la pista no alcanzó.
export const hintText = (group) => {
  const parts = [];
  if (group.with_hint) parts.push(`${group.with_hint} con pista`);
  if (group.repeated_in_turn) parts.push(`${group.repeated_in_turn} ${group.repeated_in_turn === 1 ? 'repetido' : 'repetidos'} en el mismo turno`);
  return parts.join(' · ');
};

export const sourcesText = (sources) => Object.entries(sources || {})
  .sort(([, a], [, b]) => b - a)
  .map(([source, count]) => `${SOURCE_LABELS[source] || source} ${count}`)
  .join(' · ');

const formatDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
};

const codeBoxSx = {
  mt: 1,
  p: 1.5,
  borderRadius: 1,
  border: `1px solid ${ADMIN_COLORS.border}`,
  backgroundColor: ADMIN_COLORS.page,
  color: ADMIN_COLORS.text,
  fontFamily: 'monospace',
  fontSize: '0.8rem',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  maxHeight: 220,
  overflowY: 'auto'
};

const rowSx = {
  p: 1.5,
  borderRadius: 1,
  border: `1px solid ${ADMIN_COLORS.border}`,
  backgroundColor: ADMIN_COLORS.surface
};

const countSx = {
  minWidth: 40,
  height: 28,
  px: 1,
  borderRadius: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  color: ADMIN_COLORS.accent,
  backgroundColor: ADMIN_COLORS.selectedBg,
  flexShrink: 0
};

const ErrorGroup = ({ group }) => {
  const [open, setOpen] = useState(false);
  const schema = group.category === 'schema';
  return (
    <Box sx={rowSx}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={countSx} aria-label={`${group.count} casos`}>{group.count}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ color: ADMIN_COLORS.text, fontFamily: 'monospace', fontSize: '0.85rem', overflowWrap: 'anywhere' }}>
            {group.pattern}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {schema && (
              <Chip
                size="small"
                label="Tabla o columna inexistente"
                sx={{ color: T.warning, backgroundColor: T.warning12, fontWeight: 600 }}
              />
            )}
            <Typography sx={{ color: ADMIN_COLORS.muted, fontSize: '0.8rem' }}>
              Último: {formatDate(group.last_seen)} · {sourcesText(group.sources)}
              {hintText(group) ? ` · ${hintText(group)}` : ''}
            </Typography>
          </Stack>
          <Button size="small" onClick={() => setOpen((prev) => !prev)} sx={{ ...outlinedButtonSx, border: 'none', px: 0, mt: 0.5 }}>
            {open ? 'Ocultar ejemplo' : 'Ver ejemplo'}
          </Button>
          {open && (
            <>
              <Typography sx={{ color: ADMIN_COLORS.muted, fontSize: '0.8rem', mt: 1 }}>Mensaje del motor</Typography>
              <Box sx={codeBoxSx}>{group.example_error}</Box>
              {group.example_hint && (
                <>
                  <Typography sx={{ color: ADMIN_COLORS.muted, fontSize: '0.8rem', mt: 1 }}>Pista que recibió el modelo</Typography>
                  <Box sx={codeBoxSx} aria-label="Pista de ejemplo">{group.example_hint}</Box>
                </>
              )}
              {group.example_sql && (
                <>
                  <Typography sx={{ color: ADMIN_COLORS.muted, fontSize: '0.8rem', mt: 1 }}>Consulta</Typography>
                  <Box sx={codeBoxSx} aria-label="Consulta de ejemplo">{group.example_sql}</Box>
                </>
              )}
            </>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

/**
 * Errores frecuentes del motor elegido (GET /sql-rules/{engine}/errors):
 * las consultas que el motor rechazó, agrupadas por mensaje, para escribir
 * reglas con casos reales. Los de tabla o columna inexistente se ocultan
 * por omisión: una regla del dialecto no los arregla.
 */
const SqlErrorsPanel = ({ engine }) => {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSchema, setShowSchema] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Caché por motor y días: volver a un motor no repite la búsqueda.
  const cache = useRef({});

  useEffect(() => {
    const key = `${engine.id}:${days}`;
    let cancelled = false;
    if (cache.current[key]) {
      setReport(cache.current[key]);
      setError(null);
      return undefined;
    }
    setIsLoading(true);
    setError(null);
    setReport(null);
    SqlRulesService.getErrors(engine.id, days)
      .then((next) => {
        cache.current[key] = next;
        if (!cancelled) setReport(next);
      })
      .catch((err) => { if (!cancelled) setError(err.message || 'No fue posible obtener los errores.'); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [engine.id, days, reloadKey]);

  const refresh = () => {
    delete cache.current[`${engine.id}:${days}`];
    setReloadKey((prev) => prev + 1);
  };

  const groups = (report?.groups || []).filter((group) => showSchema || group.category !== 'schema');
  const schemaCount = (report?.total || 0) - (report?.dialect_total || 0);

  return (
    <Section
      title="Errores frecuentes"
      subtitle={`Consultas que ${engine.label} rechazó, agrupadas por mensaje. Úsalas para escribir reglas a partir de casos reales.`}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
        <TextField
          select
          id="sql-errors-days"
          label="Periodo"
          value={days}
          onChange={(event) => setDays(Number(event.target.value))}
          size="small"
          sx={{ ...darkFieldSx, minWidth: { sm: 180 } }}
        >
          {DAY_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>Últimos {option} días</MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={(
            <Checkbox
              checked={showSchema}
              onChange={(event) => setShowSchema(event.target.checked)}
              sx={{ color: ADMIN_COLORS.muted, '&.Mui-checked': { color: ADMIN_COLORS.accent } }}
            />
          )}
          label={`Incluir tablas o columnas inexistentes${report ? ` (${schemaCount})` : ''}`}
          sx={{ color: ADMIN_COLORS.text, mr: 0 }}
        />
        <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
        <Button variant="outlined" onClick={refresh} disabled={isLoading} sx={outlinedButtonSx}>
          Volver a buscar
        </Button>
      </Stack>

      {isLoading && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={20} sx={{ color: ADMIN_COLORS.accent }} />
          <Typography sx={{ color: ADMIN_COLORS.muted }}>
            Buscando consultas fallidas{engine.id === 'athena' ? ' (incluye el historial de Athena, puede tardar unos segundos)' : ''}…
          </Typography>
        </Stack>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {report && (
        <>
          <Typography sx={{ color: ADMIN_COLORS.text, mb: 1.5 }}>
            {report.total === 0
              ? `Sin consultas fallidas en los últimos ${report.days} días.`
              : `${report.total} consultas fallidas en los últimos ${report.days} días; ${report.dialect_total} por el dialecto.`}
          </Typography>
          {report.truncated && (
            <Alert severity="info" sx={{ mb: 1.5 }}>Hay muchas; se revisaron las más recientes.</Alert>
          )}
          {report.total > 0 && groups.length === 0 && (
            <Typography sx={{ color: ADMIN_COLORS.muted }}>
              Todas son de tablas o columnas inexistentes. Marca la casilla para verlas.
            </Typography>
          )}
          <Stack spacing={1}>
            {groups.map((group) => <ErrorGroup key={group.pattern} group={group} />)}
          </Stack>
          {report.more_groups > 0 && (
            <Typography sx={{ color: ADMIN_COLORS.muted, mt: 1.5, fontSize: '0.85rem' }}>
              Y {report.more_groups} mensajes más con menos casos.
            </Typography>
          )}
          <Typography sx={{ color: ADMIN_COLORS.muted, mt: 2, fontSize: '0.8rem' }}>
            Se registran las consultas fallidas del chat, del Análisis Profundo y de los tableros desde que existe este panel
            {engine.id === 'athena' ? ', más el historial del workgroup de Athena' : ''}. Se conservan 45 días.
          </Typography>
        </>
      )}
    </Section>
  );
};

export default SqlErrorsPanel;
